import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { ShoppingCart, CheckCircle, Tag, CreditCard, Loader2, X, Monitor, Calendar } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom'; 

const UserPackagesPage = () => {
  //const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); 
  const [currency, setCurrency] = useState('USD');
  const [selectedPkg, setSelectedPkg] = useState(null);

  // Fetch Products
  const { data: packages = [] } = useQuery({
    queryKey: ['storeProducts'],
    queryFn: () => axiosClient.get('/store/products')
  });

  // AUTO-OPEN MODAL Logic
useEffect(() => {
      const planId = searchParams.get('plan');
      if (!planId || packages.length === 0) return;

      const pkg = packages.find(p => p.id === parseInt(planId, 10));
      if (!pkg) return;

      setSelectedPkg((current) => current || pkg);
  }, [searchParams, packages]);

  const getPrice = (pkg) => pkg.pricing?.find(p => p.currency === currency)?.amount || 0;
  const symbol = currency === 'INR' ? '₹' : '$';

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Available Plans</h1>
          <p className="text-sm text-slate-500">Choose a plan to generate a new license.</p>
        </div>
        
        <div className="bg-white border p-1 rounded-lg flex text-sm font-bold shadow-sm">
           <button onClick={() => setCurrency('USD')} className={`px-4 py-1.5 rounded-md transition-all ${currency==='USD' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>USD</button>
           <button onClick={() => setCurrency('INR')} className={`px-4 py-1.5 rounded-md transition-all ${currency==='INR' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>INR</button>
        </div>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         {packages.map(pkg => (
            <div key={pkg.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all flex flex-col relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <ShoppingCart className="w-24 h-24 text-blue-600" />
               </div>
               
               <div className="mb-4">
                  <h3 className="text-lg font-bold text-slate-800">{pkg.name}</h3>
                  <div className="flex items-baseline mt-2">
                     <span className="text-3xl font-black text-slate-900">{symbol}{getPrice(pkg)}</span>
                     <span className="text-xs text-slate-500 ml-1 uppercase font-bold">/ {pkg.billing_cycle.replace('_', ' ')}</span>
                  </div>
               </div>

               <div className="space-y-3 flex-1 mb-6">
                  <div className="flex items-center text-sm text-slate-600 bg-slate-50 p-2 rounded-lg">
                      <Calendar className="w-4 h-4 mr-2 text-blue-500"/> {pkg.duration_days} Days Validity
                  </div>
                  <div className="flex items-center text-sm text-slate-600 bg-slate-50 p-2 rounded-lg">
                      <Monitor className="w-4 h-4 mr-2 text-purple-500"/> {pkg.max_devices} Device Limit
                  </div>
                  <div className="pt-2">
                     {(typeof pkg.features_list === 'string' ? JSON.parse(pkg.features_list) : pkg.features_list || []).map((f, i) => (
                        <div key={i} className="flex items-start text-xs text-slate-500 mb-1">
                           <CheckCircle className="w-3 h-3 mr-2 text-green-500 flex-shrink-0 mt-0.5"/> {f}
                        </div>
                     ))}
                  </div>
               </div>

               <button 
                 onClick={() => setSelectedPkg(pkg)}
                 className="w-full bg-blue-600 text-white py-2.5 rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/20 active:scale-95 transition-all"
               >
                 Buy Now
               </button>
            </div>
         ))}
      </div>

      {/* CHECKOUT MODAL */}
      {selectedPkg && (
         <CheckoutModal 
            pkg={selectedPkg} 
            currency={currency} 
            symbol={symbol} 
            basePrice={getPrice(selectedPkg)}
            onClose={() => { setSelectedPkg(null); navigate('/portal/shop'); }} // Clear query param on close
         />
      )}

    </div>
  );
};

// --- Checkout Modal Component ---
const CheckoutModal = ({ pkg, currency, symbol, basePrice, onClose }) => {
    const [appliedCoupon, setAppliedCoupon] = useState(''); // The code actually sent to API
    const [manualInput, setManualInput] = useState(''); // What shows in input box
    
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // 1. Fetch Available Coupons
    const { data: coupons = [] } = useQuery({
        queryKey: ['myCoupons'],
        queryFn: () => axiosClient.get('/store/coupons/active')
    });

    // 2. Fetch Preview (Calculation) triggers when 'appliedCoupon' changes
    const { data: totals, isLoading, isFetching } = useQuery({
        queryKey: ['checkoutPreview', pkg.id, currency, appliedCoupon],
        queryFn: () => axiosClient.post('/store/checkout/preview', {
            product_id: pkg.id, currency, coupon_code: appliedCoupon || null
        }),
        keepPreviousData: true
    });

    // 3. Purchase Mutation
    const buyMutation = useMutation({
        mutationFn: () => axiosClient.post('/store/checkout/place', {
            product_id: pkg.id, currency, coupon_code: appliedCoupon || null
        }),
        onSuccess: () => {
            alert("Payment Successful! License Generated.");
            queryClient.invalidateQueries(['userLicenses']);
            queryClient.invalidateQueries(['userOrders']);
            navigate('/portal/licenses');
        },
        onError: () => alert("Transaction Failed.")
    });

    // Helper: Apply Coupon from Card or Input
    const handleApplyCode = (code) => {
        setManualInput(code); // Fill Input
        setAppliedCoupon(code); // Trigger Calculation
    };

    return (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                
                {/* Header */}
                <div className="bg-slate-50 p-5 border-b flex justify-between items-center">
                    <h3 className="font-bold text-slate-800 flex items-center text-lg">
                        <ShoppingCart className="w-5 h-5 mr-2 text-blue-600"/> Checkout
                    </h3>
                    <button onClick={onClose}><X className="w-6 h-6 text-slate-400 hover:text-slate-600"/></button>
                </div>
                
                <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
                    
                    {/* Product Summary */}
                    <div className="flex justify-between items-center pb-4 border-b border-dashed border-slate-300">
                        <div>
                            <h4 className="font-bold text-slate-800 text-lg">{pkg.name}</h4>
                            <p className="text-xs text-slate-500 font-medium flex items-center mt-1">
                                <Calendar className="w-3 h-3 mr-1"/> {pkg.duration_days} Days • <Monitor className="w-3 h-3 mr-1 ml-2"/> {pkg.max_devices} Devices
                            </p>
                        </div>
                        <span className="text-2xl font-black text-slate-700">{symbol}{basePrice}</span>
                    </div>

                    {/* ✅ PRO COUPON SECTION */}
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2 flex items-center">
                            <Tag className="w-3 h-3 mr-1"/> Promo Code
                        </label>
                        
                        {/* Input Area */}
                        <div className="flex gap-2">
                             <input 
                                className="flex-1 p-3 border border-slate-200 rounded-xl text-sm font-mono uppercase focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                placeholder="ENTER CODE"
                                value={manualInput}
                                onChange={(e) => setManualInput(e.target.value.toUpperCase())}
                             />
                             <button 
                                onClick={() => setAppliedCoupon(manualInput)}
                                className="bg-slate-800 text-white px-5 rounded-xl text-xs font-bold hover:bg-slate-700 transition-colors"
                             >
                                APPLY
                             </button>
                        </div>

                        {/* Slider Area (Only if coupons exist) */}
                        {coupons.length > 0 && (
                            <div className="mt-4">
                                <p className="text-[10px] text-slate-400 font-bold mb-2">AVAILABLE OFFERS</p>
                                <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-hide snap-x">
                                    {coupons.map((c) => (
                                        <div 
                                            key={c.code}
                                            onClick={() => handleApplyCode(c.code)}
                                            className={`
                                                min-w-[140px] p-3 rounded-xl border cursor-pointer transition-all snap-start
                                                ${appliedCoupon === c.code 
                                                    ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500' 
                                                    : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-md'
                                                }
                                            `}
                                        >
                                            <div className="flex justify-between items-start mb-1">
                                                <span className={`font-bold text-xs px-1.5 py-0.5 rounded ${appliedCoupon === c.code ? 'bg-blue-200 text-blue-800' : 'bg-slate-100 text-slate-600'}`}>
                                                    {c.code}
                                                </span>
                                                {appliedCoupon === c.code && <CheckCircle className="w-4 h-4 text-blue-600" />}
                                            </div>
                                            <p className="text-sm font-bold text-slate-800">
                                                {c.discount_type === 'percentage' ? `${c.discount_value}% OFF` : `${symbol}${c.discount_value} OFF`}
                                            </p>
                                            <p className="text-[10px] text-slate-500 mt-1 line-clamp-1">
                                                {c.description || "Limited time offer"}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Totals Breakdown */}
                    <div className="bg-slate-50 p-5 rounded-2xl space-y-2 text-sm border border-slate-100">
                        {isLoading || isFetching ? (
                            <div className="flex items-center justify-center py-4 text-slate-400">
                                <Loader2 className="w-5 h-5 animate-spin mr-2"/> Calculating Total...
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-between text-slate-600"><span>Subtotal</span> <span>{symbol}{totals?.base_price}</span></div>
                                
                                {totals?.discount_amount > 0 ? (
                                    <div className="flex justify-between text-green-600 font-bold bg-green-50 p-2 rounded-lg border border-green-100">
                                        <span className="flex items-center"><Tag className="w-3 h-3 mr-1"/> Coupon Applied</span> 
                                        <span>-{symbol}{totals.discount_amount}</span>
                                    </div>
                                ) : (
                                    appliedCoupon && (
                                        <div className="text-xs text-red-500 text-center py-1">Invalid or Expired Coupon</div>
                                    )
                                )}

                                <div className="flex justify-between text-slate-500">
                                    <span>{totals?.tax_name} ({totals?.tax_percent}%)</span> 
                                    <span>{symbol}{totals?.tax_amount}</span>
                                </div>
                                
                                <div className="border-t border-slate-200 pt-3 mt-2 flex justify-between items-end">
                                    <span className="text-slate-600 font-medium">Total Payable</span> 
                                    <span className="text-2xl font-black text-slate-900 leading-none">{symbol}{totals?.total_amount}</span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Pay Button */}
                    <button 
                        onClick={() => buyMutation.mutate()}
                        disabled={buyMutation.isLoading || isLoading}
                        className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 flex justify-center items-center transition-transform active:scale-95"
                    >
                        {buyMutation.isLoading ? <Loader2 className="w-6 h-6 animate-spin mr-2"/> : <CreditCard className="w-6 h-6 mr-2"/>}
                        Pay Securely {totals?.total_amount ? `${symbol}${totals.total_amount}` : ''}
                    </button>

                </div>
            </div>
        </div>
    );
};

export default UserPackagesPage;